import { createContext, useContext, useEffect, useRef, type ReactNode, type MutableRefObject } from 'react';

// Lets any page with an editable form register "I have unsaved changes" so the
// hash-router (App.tsx) can pause navigation and prompt before leaving. A ref
// (not state) so registering dirty status on every keystroke doesn't re-render
// anything outside the page itself.
interface NavigationGuardContextValue {
  isDirtyRef: MutableRefObject<boolean>;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const isDirtyRef = useRef(false);
  return (
    <NavigationGuardContext.Provider value={{ isDirtyRef }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuardRef() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error('useNavigationGuardRef must be used within NavigationGuardProvider');
  return ctx.isDirtyRef;
}

// Call from any page that tracks its own "dirty" form state. Guards both
// in-app navigation (hash changes, handled in App.tsx) and tab close/refresh
// (native beforeunload prompt).
export function useUnsavedChangesGuard(isDirty: boolean) {
  const isDirtyRef = useNavigationGuardRef();

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty, isDirtyRef]);

  // Clear on unmount so a clean page navigated to next isn't blocked by a
  // stale flag left behind by the page that just left.
  useEffect(() => () => { isDirtyRef.current = false; }, [isDirtyRef]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirtyRef]);
}
